import React, { useState, useEffect } from 'react';
import './ShareModal.css';

const ShareModal = ({ isOpen, onClose, noteId, noteTitle, store }) => {
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [permissionType, setPermissionType] = useState('view');
  const [copySuccess, setCopySuccess] = useState(false);

  useEffect(() => {
    if (isOpen && noteId) {
      loadShares();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, noteId]);

  const loadShares = async () => {
    setLoading(true);
    try {
      const result = await store.listNoteShares(noteId);
      setShares(result.shares || []);
    } catch (error) {
      console.error('Error loading shares:', error);
      alert('Erro ao carregar compartilhamentos');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateLinkShare = async () => {
    setLoading(true);
    try {
      const result = await store.createNoteShare(noteId, {
        shareType: 'link',
        permission: permissionType
      });

      if (result.existing) {
        alert('Um link de compartilhamento já existe para esta nota');
      }

      await loadShares();
    } catch (error) {
      console.error('Error creating link share:', error);
      alert('Erro ao criar link de compartilhamento');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEmailShare = async () => {
    if (!emailInput.trim()) {
      alert('Digite um e-mail válido');
      return;
    }

    setLoading(true);
    try {
      const result = await store.createNoteShare(noteId, {
        shareType: 'email',
        permission: permissionType,
        email: emailInput.trim()
      });

      if (result.existing) {
        alert('Este e-mail já tem acesso a esta nota');
      } else {
        alert('Convite enviado! O usuário terá acesso quando fizer login.');
        setEmailInput('');
      }

      await loadShares();
    } catch (error) {
      console.error('Error creating email share:', error);
      alert('Erro ao compartilhar por e-mail');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleLinkEnabled = async (share) => {
    setLoading(true);
    try {
      await store.updateNoteShare(noteId, share.id, {
        linkEnabled: !share.linkEnabled
      });
      await loadShares();
    } catch (error) {
      console.error('Error toggling link:', error);
      alert('Erro ao atualizar link');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePermission = async (share, newPermission) => {
    setLoading(true);
    try {
      await store.updateNoteShare(noteId, share.id, {
        permission: newPermission
      });
      await loadShares();
    } catch (error) {
      console.error('Error changing permission:', error);
      alert('Erro ao alterar permissão');
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeShare = async (share) => {
    const confirmMsg = share.shareType === 'link'
      ? 'Tem certeza que deseja revogar o link de compartilhamento?'
      : `Tem certeza que deseja remover o acesso de ${share.sharedWithEmail}?`;

    if (!window.confirm(confirmMsg)) return;

    setLoading(true);
    try {
      await store.deleteNoteShare(noteId, share.id);
      await loadShares();
    } catch (error) {
      console.error('Error revoking share:', error);
      alert('Erro ao revogar compartilhamento');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = (linkToken) => {
    const shareUrl = `${window.location.origin}/shared/${linkToken}`;
    navigator.clipboard.writeText(shareUrl)
      .then(() => {
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      })
      .catch(err => {
        console.error('Error copying link:', err);
        alert('Erro ao copiar link');
      });
  };

  const linkShare = shares.find(s => s.shareType === 'link');
  const emailShares = shares.filter(s => s.shareType === 'email');

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" onClick={onClose} />
      <div className="share-modal">
        <div className="modal-header">
          <h3>Compartilhar: {noteTitle}</h3>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="modal-content">
          {loading && <div className="loading-spinner">Carregando...</div>}

          {/* Link Sharing Section */}
          <section className="share-section">
            <h4><i className="fa-solid fa-link"></i> Compartilhar via Link</h4>
            <p className="section-description">Qualquer pessoa com o link poderá acessar esta nota</p>

            {linkShare ? (
              <div className="share-item link-share">
                <div className="share-info">
                  <div className="share-url">
                    <code>{window.location.origin}/shared/{linkShare.linkToken}</code>
                    <button
                      className="copy-btn"
                      onClick={() => handleCopyLink(linkShare.linkToken)}
                      title="Copiar link"
                    >
                      <i className={copySuccess ? "fa-solid fa-check" : "fa-solid fa-copy"}></i>
                    </button>
                  </div>
                  <div className="share-meta">
                    <span className="share-status">
                      {linkShare.linkEnabled ? (
                        <span className="status-enabled">✅ Ativo</span>
                      ) : (
                        <span className="status-disabled">🔒 Desativado</span>
                      )}
                    </span>
                    <span className="share-permission">
                      Permissão: {linkShare.permission === 'edit' ? '✏️ Edição' : '👁️ Visualização'}
                    </span>
                    <span className="share-access">
                      Acessos: {linkShare.accessCount}
                    </span>
                  </div>
                </div>
                <div className="share-actions">
                  <button
                    className="btn-secondary"
                    onClick={() => handleToggleLinkEnabled(linkShare)}
                    disabled={loading}
                  >
                    {linkShare.linkEnabled ? 'Desativar' : 'Ativar'}
                  </button>
                  <select
                    value={linkShare.permission}
                    onChange={(e) => handleChangePermission(linkShare, e.target.value)}
                    disabled={loading}
                  >
                    <option value="view">Visualização</option>
                    <option value="edit">Edição</option>
                  </select>
                  <button
                    className="btn-danger"
                    onClick={() => handleRevokeShare(linkShare)}
                    disabled={loading}
                  >
                    Revogar
                  </button>
                </div>
              </div>
            ) : (
              <div className="create-share">
                <select
                  value={permissionType}
                  onChange={(e) => setPermissionType(e.target.value)}
                  disabled={loading}
                >
                  <option value="view">Visualização</option>
                  <option value="edit">Edição</option>
                </select>
                <button
                  className="btn-primary"
                  onClick={handleCreateLinkShare}
                  disabled={loading}
                >
                  Criar Link de Compartilhamento
                </button>
              </div>
            )}
          </section>

          {/* Email Sharing Section */}
          <section className="share-section">
            <h4><i className="fa-solid fa-envelope"></i> Compartilhar via E-mail</h4>
            <p className="section-description">Compartilhe com pessoas específicas por e-mail</p>

            <div className="create-share">
              <input
                type="email"
                placeholder="usuario@exemplo.com"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                disabled={loading}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleCreateEmailShare();
                }}
              />
              <select
                value={permissionType}
                onChange={(e) => setPermissionType(e.target.value)}
                disabled={loading}
              >
                <option value="view">Visualização</option>
                <option value="edit">Edição</option>
              </select>
              <button
                className="btn-primary"
                onClick={handleCreateEmailShare}
                disabled={loading}
              >
                Convidar
              </button>
            </div>

            {emailShares.length > 0 && (
              <div className="email-shares-list">
                <h5>Pessoas com acesso ({emailShares.length})</h5>
                {emailShares.map(share => (
                  <div key={share.id} className="share-item email-share">
                    <div className="share-info">
                      <div className="share-email">
                        <i className="fa-solid fa-user"></i>
                        {share.sharedWithEmail}
                        {!share.sharedWithUserId && (
                          <span className="pending-badge">Pendente</span>
                        )}
                      </div>
                      <span className="share-permission">
                        {share.permission === 'edit' ? '✏️ Edição' : '👁️ Visualização'}
                      </span>
                    </div>
                    <div className="share-actions">
                      <select
                        value={share.permission}
                        onChange={(e) => handleChangePermission(share, e.target.value)}
                        disabled={loading}
                      >
                        <option value="view">Visualização</option>
                        <option value="edit">Edição</option>
                      </select>
                      <button
                        className="btn-danger"
                        onClick={() => handleRevokeShare(share)}
                        disabled={loading}
                      >
                        Remover
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Fechar</button>
        </div>
      </div>
    </>
  );
};

export default ShareModal;
